/**
 * Отдельный vuex модуль для взаимодействия по analytics/fall_detection-API
 */

import {ANALYTICS, AnalyticSettings} from "@/store/analytics/helpers.js";
import makeMessage from "@/store/analytics/analyticMessage.js";

// actions для fall_detections. Вызов действия начинать с "analytics/fallDetection/"
export const ACTION_LOAD_FALL_DETECTIONS_SETTINGS = "LOAD_FALL_DETECTIONS_SETTINGS";
export const ACTION_LOAD_FALL_DETECTION_SETTINGS = "LOAD_FALL_DETECTION_SETTINGS";
export const ACTION_LOAD_FALL_DETECTION_EVENTS = "LOAD_FALL_DETECTION_EVENTS";
export const ACTION_LOAD_FALL_DETECTION_EVENT = "LOAD_FALL_DETECTION_EVENT";
export const ACTION_LOAD_FALL_DETECTION_REPORT = "LOAD_FALL_DETECTION_REPORT";

/**
 * Класс для представления настроек аналитики обнаружения падения.
 */
export class FallDetectionSettings extends AnalyticSettings {
  get analyticType() {
    return ANALYTICS.fall_detection;
  }
}

export default {
  namespaced: true,
  state: {},
  mutations: {},
  actions: {
    /**
         * Загрузка списка настроек аналитик обнаружения падения.
         *
         * @param {Object} context
         * @param {Array.<String>} camerasNumbers
         * @return {Promise.<Array.<FallDetectionSettings>>}
         */
    async [ACTION_LOAD_FALL_DETECTIONS_SETTINGS](context, {camerasNumbers}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/fall_detection/settings/", {camera_numbers: camerasNumbers});
        return response.data.results.map(rawFallDetectionSettings => new FallDetectionSettings(rawFallDetectionSettings));
      } catch {
        return [];
      }
    },
    /**
         * Загрузка настроек аналитики обнаружения падения с конкретной камеры.
         *
         * @param {Function} dispatch
         * @param {String} cameraNumber
         * @return {Promise.<FallDetectionSettings>}
         */
    async [ACTION_LOAD_FALL_DETECTION_SETTINGS]({dispatch}, {cameraNumber}) {
      const fallDetectionsSettings = await dispatch(ACTION_LOAD_FALL_DETECTIONS_SETTINGS, {camerasNumbers: [cameraNumber]});
      if (_.isEmpty(fallDetectionsSettings)) {
        return null;
      }
      return fallDetectionsSettings[0];
    },
    /**
         * Загрузка перечня событий по аналитике обнаружения падения с камеры для формирования отчета.
         *
         * @param {Object} context
         * @param {String} cameraNumber
         * @param {Array.<Number>} eventIds
         * @return {Promise.<Array.<FallDetectionMessage>>}
         */
    async [ACTION_LOAD_FALL_DETECTION_EVENTS](context, {cameraNumber, eventIds}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/fall_detection/events/", {
          camera_number: cameraNumber,
          event_ids: eventIds,
        });
        return response.data.results.map(rawEventData => makeMessage(rawEventData, cameraNumber, ANALYTICS.fall_detection));
      } catch {
        return [];
      }
    },
    /**
         * Загрузка перечня событий по аналитике обнаружения падения с камеры для формирования отчета.
         *
         * @param {Function} dispatch
         * @param {String} cameraNumber
         * @param {Number} eventId
         * @return {Promise.<FallDetectionMessage>}
         */
    async [ACTION_LOAD_FALL_DETECTION_EVENT]({dispatch}, {cameraNumber, eventId}) {
      const fallDetectionsSettings = await dispatch(ACTION_LOAD_FALL_DETECTION_EVENTS, {cameraNumber, eventIds: [eventId]});
      if (_.isEmpty(fallDetectionsSettings)) {
        return null;
      }
      return fallDetectionsSettings[0];
    },
    /**
         * Загрузка перечня событий по определению обнаружения падения с камеры для формирования отчета.
         *
         * @param {Object} context
         * @param {String} cameraNumber
         * @param {Date} archiveFrom
         * @param {Date} archiveTo
         * @param {String} query
         * @return {Promise.<Array.<FallDetectionMessage>>}
         */
    async [ACTION_LOAD_FALL_DETECTION_REPORT](context, {cameraNumber, archiveFrom, archiveTo, query = null, page, page_size, cancelToken}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/fall_detection/report/", {
          camera_number: cameraNumber,
          start: archiveFrom.toISOString(),
          end: archiveTo.toISOString(),
          query: query,
          page: page,
          page_size: page_size
        }, {cancelToken});
        const messages = response.data.results.map(rawEventData => makeMessage(rawEventData, cameraNumber, ANALYTICS.fall_detection));

        return {
          messages,
          response: response.data
        };
      } catch {
        return {
          messages: [],
          response: null
        };
      }
    },
  },
};
